import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

const HIGH_CONTRAST_KEY = 'zadig-drawer-high-contrast';

/**
 * Zadig drawer.
 *
 * Desktop: multi-column flyout (each open level = a column to the right).
 * Mobile : push panels (each level slides over the previous from the right).
 *
 * The overlay + panel-stack are portaled to <body> at mount time so they
 * escape the header's `contain: layout` containing block and can cover the
 * full viewport (announcement bar included).
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLDivElement} panelStack
 *
 * @extends {Component<Refs>}
 */
class ZadigDrawer extends Component {
  requiredRefs = ['details', 'panelStack'];

  #portaled = false;
  #movedOverlay = null;
  #movedPanelStack = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keyup', this.#onKeyUp);
    this.#restoreContrast();
    this.#portalToBody();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keyup', this.#onKeyUp);
    this.#unportal();
  }

  /** Move overlay + panel-stack out of the header's containing block. */
  #portalToBody() {
    if (this.#portaled) return;

    this.#movedOverlay = this.querySelector('.zadig-drawer__overlay');
    this.#movedPanelStack = this.refs.panelStack;
    if (!this.#movedOverlay || !this.#movedPanelStack) return;

    document.body.appendChild(this.#movedOverlay);
    document.body.appendChild(this.#movedPanelStack);
    this.#portaled = true;

    this.#bindMoved(this.#movedOverlay);
    this.#bindMoved(this.#movedPanelStack);
  }

  #unportal() {
    this.#movedOverlay?.parentNode?.removeChild(this.#movedOverlay);
    this.#movedPanelStack?.parentNode?.removeChild(this.#movedPanelStack);
    this.#movedOverlay = null;
    this.#movedPanelStack = null;
    this.#portaled = false;
  }

  /**
   * Re-bind on:click handlers on a moved subtree directly to this instance.
   * The framework dispatches via closest()/closest-component, both of which
   * fail once the element is no longer a descendant of <zadig-drawer>.
   */
  #bindMoved(root) {
    const targets = [];
    if (root.matches?.('[on\\:click]')) targets.push(root);
    targets.push(...root.querySelectorAll('[on\\:click]'));

    targets.forEach((el) => {
      const raw = el.getAttribute('on:click') || '';
      const slashIndex = raw.lastIndexOf('/');
      if (slashIndex < 0) return;
      const method = raw.slice(slashIndex + 1).replace(/\?.*/, '');

      el.removeAttribute('on:click'); // prevent double-firing via framework

      el.addEventListener('click', (event) => {
        const fn = this[method];
        if (typeof fn === 'function') fn.call(this, event);
      });
    });
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  toggle(event) {
    event?.preventDefault?.();
    return this.isOpen ? this.close() : this.open();
  }

  open() {
    const { details } = this.refs;
    const summary = details.querySelector('summary');

    summary?.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');

    requestAnimationFrame(() => {
      details.classList.add('zadig-drawer--open');
      this.#movedPanelStack?.classList.add('is-open');
      this.#movedOverlay?.classList.add('is-open');
      document.documentElement.setAttribute('scroll-lock', '');

      const root = this.#getPanel('root');
      if (root) trapFocus(root);
    });
  }

  close() {
    const { details } = this.refs;
    const summary = details.querySelector('summary');
    const panelStack = this.#movedPanelStack;
    const overlay = this.#movedOverlay;

    summary?.setAttribute('aria-expanded', 'false');
    details.classList.remove('zadig-drawer--open');
    panelStack?.classList.remove('is-open');
    overlay?.classList.remove('is-open');

    if (!panelStack) return;

    const onTransitionEnd = (event) => {
      if (event.target !== panelStack || event.propertyName !== 'transform') return;
      panelStack.removeEventListener('transitionend', onTransitionEnd);

      details.removeAttribute('open');
      document.documentElement.removeAttribute('scroll-lock');
      this.#resetPanels();
      removeTrapFocus();
    };

    panelStack.addEventListener('transitionend', onTransitionEnd);
  }

  /** Open a child panel (and its companion grid panel, if any). */
  openPanel(event) {
    event?.preventDefault?.();
    const trigger = event?.target?.closest('[data-panel-target]');
    if (!trigger) return;

    const targetId = trigger.getAttribute('data-panel-target');
    const target = this.#getPanel(targetId);
    if (!target) return;

    const targetLevel = parseInt(target.dataset.level, 10) || 1;
    const wasActive = target.hasAttribute('data-active');

    this.#allPanels().forEach((panel) => {
      const level = parseInt(panel.dataset.level, 10) || 0;
      if (level >= targetLevel) {
        panel.removeAttribute('data-active');
        panel.setAttribute('inert', '');
      }
    });
    this.#getPanelStackRoot()
      .querySelectorAll('[data-panel-target][aria-expanded="true"]')
      .forEach((btn) => {
        const id = btn.getAttribute('data-panel-target');
        const lvl = parseInt(this.#getPanel(id)?.dataset.level, 10) || 0;
        if (lvl >= targetLevel) btn.setAttribute('aria-expanded', 'false');
      });

    this.#refreshActiveChildMarkers();

    if (wasActive) {
      this.#refreshActiveChildMarkers();
      return;
    }

    target.setAttribute('data-active', '');
    target.removeAttribute('inert');
    trigger.setAttribute('aria-expanded', 'true');

    const companion = this.#getPanelStackRoot().querySelector(
      `[data-grid-for="${CSS.escape(targetId)}"]`
    );
    if (companion) {
      companion.setAttribute('data-active', '');
      companion.removeAttribute('inert');
    }

    const parentPanel = trigger.closest('[data-panel-id]');
    if (parentPanel) parentPanel.setAttribute('data-has-active-child', '');

    trapFocus(target);
  }

  /** Mobile-only: pop the deepest open panel. */
  back(event) {
    event?.preventDefault?.();

    const open = this.#allPanels()
      .filter((p) => p.hasAttribute('data-active') && !p.hasAttribute('data-grid-for'))
      .sort((a, b) => parseInt(b.dataset.level, 10) - parseInt(a.dataset.level, 10));

    if (open.length === 0) return this.close();

    const top = open[0];
    const topId = top.getAttribute('data-panel-id');

    top.removeAttribute('data-active');
    top.setAttribute('inert', '');

    const companion = this.#getPanelStackRoot().querySelector(
      `[data-grid-for="${CSS.escape(topId)}"]`
    );
    if (companion) {
      companion.removeAttribute('data-active');
      companion.setAttribute('inert', '');
    }

    const trigger = this.#getPanelStackRoot().querySelector(
      `[data-panel-target="${CSS.escape(topId)}"]`
    );
    trigger?.setAttribute('aria-expanded', 'false');

    this.#refreshActiveChildMarkers();

    const parent = open[1] || this.#getPanel('root');
    if (parent) trapFocus(parent);
  }

  /** Toggle the high-contrast mode (persists across sessions). */
  toggleContrast(event) {
    event?.preventDefault?.();
    const html = document.documentElement;
    const next = !html.hasAttribute('data-high-contrast');
    if (next) {
      html.setAttribute('data-high-contrast', '');
    } else {
      html.removeAttribute('data-high-contrast');
    }
    try {
      localStorage.setItem(HIGH_CONTRAST_KEY, next ? '1' : '0');
    } catch (_) {}
    this.#getPanelStackRoot()
      .querySelectorAll('.zadig-drawer__a11y')
      .forEach((btn) => btn.setAttribute('aria-pressed', next ? 'true' : 'false'));
  }

  #restoreContrast() {
    try {
      if (localStorage.getItem(HIGH_CONTRAST_KEY) === '1') {
        document.documentElement.setAttribute('data-high-contrast', '');
      }
    } catch (_) {}
  }

  /** Root for queries on the panel stack — works whether portaled or not. */
  #getPanelStackRoot() {
    return this.#movedPanelStack || this;
  }

  #refreshActiveChildMarkers() {
    const root = this.#getPanelStackRoot();
    root.querySelectorAll('[data-has-active-child]').forEach((p) => {
      p.removeAttribute('data-has-active-child');
    });
    root.querySelectorAll('[data-panel-target][aria-expanded="true"]').forEach((btn) => {
      const parentPanel = btn.closest('[data-panel-id]');
      if (parentPanel) parentPanel.setAttribute('data-has-active-child', '');
    });
  }

  #allPanels() {
    return Array.from(this.#getPanelStackRoot().querySelectorAll('[data-panel-id]'));
  }

  #getPanel(id) {
    return this.#getPanelStackRoot().querySelector(`[data-panel-id="${CSS.escape(id)}"]`);
  }

  #resetPanels() {
    this.#allPanels().forEach((panel) => {
      if (panel.dataset.panelId === 'root') return;
      panel.removeAttribute('data-active');
      panel.setAttribute('inert', '');
    });
    const root = this.#getPanelStackRoot();
    root.querySelectorAll('[data-panel-target]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    root.querySelectorAll('[data-has-active-child]').forEach((p) => {
      p.removeAttribute('data-has-active-child');
    });
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    const hasOpenChild = this.#allPanels().some(
      (p) => p.dataset.panelId !== 'root' && p.hasAttribute('data-active')
    );

    if (hasOpenChild) {
      this.back(event);
    } else {
      this.close();
    }
  };
}

if (!customElements.get('zadig-drawer')) {
  customElements.define('zadig-drawer', ZadigDrawer);
}
